import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ResponsiveLine } from '@nivo/line';
import Card from '../ui/Card/Card';
import Button from '../ui/Button/Button';
import Spinner from '../ui/Spinner/Spinner';
import { getIncubations } from '../../controllers/incubations';
import { getEngagementsByDate } from '../../controllers/analytics';
import { useUser } from '../../contexts/UserContext';
import './Dashboard.css';



const Dashboard = () => {
  const navigate = useNavigate();
  const { hasGlobalFunctionality, hasIncubationAccess, isDashboardAdmin } = useUser();
  const [activeIncubations, setActiveIncubations] = useState([]);
  const [loading, setLoading] = useState(true);

  // Engagement chart state
  const [engagementData, setEngagementData] = useState([]);
  const [engagementsLoading, setEngagementsLoading] = useState(true);
  const [dateRange, setDateRange] = useState({
    start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    fetchActiveIncubations();
  }, []);

  useEffect(() => {
    fetchEngagementData();
  }, [dateRange]);

  const fetchActiveIncubations = async () => {
    try {
      setLoading(true);
      const response = await getIncubations({ status: 'active' });

      if (response.data && response.data.success) {
        setActiveIncubations(response.data.incubations || []);
      } else {
        console.error('Failed to fetch active incubations:', response.data?.error);
        setActiveIncubations([]);
      }
    } catch (error) {
      console.error('Error fetching active incubations:', error);
      setActiveIncubations([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchEngagementData = async () => {
    try {
      setEngagementsLoading(true);
      const response = await getEngagementsByDate({
        start_date: dateRange.start,
        end_date: dateRange.end
      });

      if (response.data?.success && response.data?.data?.daily_counts) {
        // Transform to Nivo format
        const chartData = [{
          id: 'Engagements',
          color: '#3b82f6',
          data: response.data.data.daily_counts.map(item => ({
            x: formatDateLabel(item.date),
            y: item.count
          }))
        }];
        setEngagementData(chartData);
      } else {
        setEngagementData([]);
      }
    } catch (error) {
      console.error('Error fetching engagement data:', error);
      setEngagementData([]);
    } finally {
      setEngagementsLoading(false);
    }
  };

  const formatDateLabel = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const handleRowClick = (incubationId) => {
    navigate('/incubation', { state: { incubationId: incubationId } });
  };

  const getStatusClassName = (status) => {
    // Map database status values to CSS class names
    switch (status?.toLowerCase()) {
      case 'pre_launch':
        return 'prelaunch';
      case 'live':
      case 'active':
        return 'live';
      case 'completed':
        return 'completed';
      case 'archived':
        return 'archived';
      default:
        return 'prelaunch'; // Default to pre-launch styling
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: '2-digit', 
      year: 'numeric' 
    });
  };

  const getFilteredActiveIncubations = () => {
    if (isDashboardAdmin() || hasGlobalFunctionality('manage_dashboard')) {
      return activeIncubations;
    }
    return activeIncubations.filter(incubation => hasIncubationAccess(incubation.id));
  };

  const filteredActiveIncubations = getFilteredActiveIncubations();

  return (
    <div className="dashboard-container">
      <div className="dashboard-content">
        <div className="cards-row">
          <div className="top-row">
            {!loading && (
            <Card className="chart-section incubations-card">
              <div className="card-header">
                <h2 className="chart-title">Active Incubations</h2>
              </div>
              <div className={`incubations-table-container animate-in`}>
                <table className="incubations-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Start Date</th>
                      <th>End Date</th>
                      <th><i className="fas fa-users"></i></th>
                      <th>Status</th>
                      <th>Progress</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredActiveIncubations.length === 0 ? (
                      <tr>
                        <td colSpan="6" style={{ textAlign: 'center', padding: '20px', color: '#6b7280' }}>
                          No active incubations found
                        </td>
                      </tr>
                    ) : (
                      filteredActiveIncubations.map((incubation, index) => (
                        <tr
                          key={incubation.id || index}
                          onClick={() => handleRowClick(incubation.id)}
                          className="incubation-row"
                        >
                          <td>{incubation.name}</td>
                          <td>{formatDate(incubation.start_date)}</td>
                          <td>{incubation.end_date ? formatDate(incubation.end_date) : '-'}</td>
                          <td className="participants-cell">
                            <span className="participants-count">{incubation.participant_count ?? incubation.participants ?? 0}</span>
                          </td>
                          <td>
                            <span className={`status-badge ${getStatusClassName(incubation.status)}`}>
                              {incubation.status?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                            </span>
                          </td>
                          <td className="progress-cell">
                            {incubation.target_completes && incubation.data_fields?.some(f => f.name === 'completed') ? (
                              <div className="progress-bar-container">
                                <div
                                  className="progress-bar-fill"
                                  style={{ width: `${Math.min(100, ((incubation.completed_count || 0) / incubation.target_completes) * 100)}%` }}
                                />
                              </div>
                            ) : null}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </Card>
            )}
            
          </div>
          
          <Card className="chart-section">
          <div className="chart-header">
            <h2 className="chart-title">Total Engagements</h2>
            <div className="date-range-picker">
              <input
                type="date"
                value={dateRange.start}
                onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                className="date-input"
              />
              <span className="date-separator">to</span>
              <input
                type="date"
                value={dateRange.end}
                onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                className="date-input"
              />
            </div>
          </div>
          <div className="chart-container">
            {engagementsLoading ? (
              <div className="chart-loading"><Spinner /></div>
            ) : engagementData.length > 0 && engagementData[0].data.length > 0 ? (
              <ResponsiveLine
                data={engagementData}
                margin={{ top: 35, right: 20, bottom: 50, left: 60 }}
                xScale={{ type: 'point' }}
                yScale={{ type: 'linear', min: 0, max: 'auto', stacked: false }}
                curve="catmullRom"
                colors={['#3b82f6']}
                lineWidth={3}
                axisTop={null}
                axisRight={null}
                axisBottom={{
                  tickSize: 5,
                  tickPadding: 5,
                  tickRotation: -45,
                  legend: 'Date',
                  legendOffset: 40,
                  legendPosition: 'middle'
                }}
                axisLeft={{
                  tickSize: 5,
                  tickPadding: 5,
                  legend: 'Engagements',
                  legendOffset: -45,
                  legendPosition: 'middle'
                }}
                pointSize={8}
                pointColor={'white'}
                pointBorderWidth={3}
                pointBorderColor={'#3b82f6'}
                useMesh={true}
                legends={[]}
              />
            ) : (
              <div className="no-data-message">No engagement data available for the selected date range</div>
            )}
          </div>
          </Card>
          

        </div>
      </div>
    </div>
  );
};

export default Dashboard;